import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, ProgressBar, Alert, Form, Button } from 'react-bootstrap';
import { FaChartLine, FaUsers, FaFileAlt, FaEuroSign, FaCalendarAlt, FaDownload, FaEye, FaCheckCircle, FaClock, FaTimesCircle } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';

const SuperAdminStatistics = () => {
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('month');
    const [detailedStats, setDetailedStats] = useState([]);
    const [revenueData, setRevenueData] = useState([]);

    useEffect(() => {
        loadStatistics();
    }, [period]);

    const loadStatistics = async () => {
        try {
            const [overviewRes, detailedRes, revenueRes] = await Promise.all([
                api.get('/superadmin/statistics/overview/', { params: { period } }),
                api.get('/superadmin/statistics/detailed/', { params: { period } }),
                api.get('/superadmin/statistics/revenue/', { params: { period } }),
            ]);

            setStats(overviewRes.data);
            setDetailedStats(detailedRes.data);
            setRevenueData(revenueRes.data);
        } catch (error) {
            console.error('Error loading statistics:', error);
            toast.error('Erreur lors du chargement des statistiques');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const response = await api.get('/superadmin/statistics/export/', {
                params: { period },
                responseType: 'blob',
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `statistics_${period}_${new Date().toISOString().split('T')[0]}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Statistiques exportées avec succès');
        } catch (error) {
            console.error('Error exporting statistics:', error);
            toast.error('Erreur lors de l\'export');
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
        }).format(amount);
    };

    const formatPercentage = (value, total) => {
        if (total === 0) return '0%';
        return ((value / total) * 100).toFixed(1) + '%';
    };

    if (loading) {
        return (
            <Container className="py-4">
                <div className="text-center py-5">
                    <div className="spinner-border text-primary"></div>
                    <p className="mt-3">Chargement des statistiques...</p>
                </div>
            </Container>
        );
    }

    return (
        <Container className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <FaChartLine className="me-3" />
                        Statistiques Détaillées
                    </h2>
                    <p className="text-muted">Analyse complète des performances de la plateforme</p>
                </Col>
                <Col className="text-end">
                    <Form.Select
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                        className="d-inline-block w-auto me-2"
                    >
                        <option value="week">Cette semaine</option>
                        <option value="month">Ce mois</option>
                        <option value="quarter">Ce trimestre</option>
                        <option value="year">Cette année</option>
                    </Form.Select>
                    <Button variant="success" onClick={handleExport}>
                        <FaDownload className="me-2" />
                        Exporter
                    </Button>
                </Col>
            </Row>

            {/* Cartes principales */}
            <Row className="mb-4">
                <Col md={3}>
                    <Card className="text-center shadow-sm border-primary">
                        <Card.Body>
                            <FaUsers size={40} className="text-primary mb-3" />
                            <h3 className="text-primary">{stats.total_users || 0}</h3>
                            <p className="text-muted mb-1">Utilisateurs totaux</p>
                            <small className="text-success">
                                +{stats.new_users_period || 0} ce {period === 'week' ? 'semaine' : period === 'month' ? 'mois' : period === 'quarter' ? 'trimestre' : 'année'}
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm border-info">
                        <Card.Body>
                            <FaFileAlt size={40} className="text-info mb-3" />
                            <h3 className="text-info">{stats.total_dossiers || 0}</h3>
                            <p className="text-muted mb-1">Dossiers totaux</p>
                            <small className="text-success">
                                +{stats.new_dossiers_period || 0} ce {period === 'week' ? 'semaine' : period === 'month' ? 'mois' : period === 'quarter' ? 'trimestre' : 'année'}
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm border-success">
                        <Card.Body>
                            <FaEuroSign size={40} className="text-success mb-3" />
                            <h3 className="text-success">{formatCurrency(stats.total_revenue || 0)}</h3>
                            <p className="text-muted mb-1">Revenus totaux</p>
                            <small className="text-success">
                                +{formatCurrency(stats.revenue_period || 0)} ce {period === 'week' ? 'semaine' : period === 'month' ? 'mois' : period === 'quarter' ? 'trimestre' : 'année'}
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={3}>
                    <Card className="text-center shadow-sm border-warning">
                        <Card.Body>
                            <FaCheckCircle size={40} className="text-warning mb-3" />
                            <h3 className="text-warning">{formatPercentage(stats.approved_dossiers || 0, stats.total_dossiers || 1)}</h3>
                            <p className="text-muted mb-1">Taux d'approbation</p>
                            <small className="text-muted">
                                {stats.approved_dossiers || 0} approuvés sur {stats.total_dossiers || 0}
                            </small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Graphiques et métriques détaillées */}
            <Row className="mb-4">
                <Col md={8}>
                    <Card>
                        <Card.Header>
                            <h5>Évolution des dossiers</h5>
                        </Card.Header>
                        <Card.Body>
                            <Alert variant="info">
                                <FaEye className="me-2" />
                                Graphique d'évolution à implémenter avec Chart.js ou Recharts
                            </Alert>
                            <div className="text-center py-4">
                                <p className="text-muted">Graphique interactif des dossiers créés, en cours et approuvés</p>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card>
                        <Card.Header>
                            <h5>Répartition par statut</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <small>En attente</small>
                                    <small>{stats.pending_dossiers || 0}</small>
                                </div>
                                <ProgressBar
                                    variant="warning"
                                    now={formatPercentage(stats.pending_dossiers || 0, stats.total_dossiers || 1).replace('%', '')}
                                />
                            </div>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <small>En cours de traitement</small>
                                    <small>{stats.in_review_dossiers || 0}</small>
                                </div>
                                <ProgressBar
                                    variant="info"
                                    now={formatPercentage(stats.in_review_dossiers || 0, stats.total_dossiers || 1).replace('%', '')}
                                />
                            </div>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <small>Approuvés</small>
                                    <small>{stats.approved_dossiers || 0}</small>
                                </div>
                                <ProgressBar
                                    variant="success"
                                    now={formatPercentage(stats.approved_dossiers || 0, stats.total_dossiers || 1).replace('%', '')}
                                />
                            </div>
                            <div className="mb-3">
                                <div className="d-flex justify-content-between mb-1">
                                    <small>Rejetés</small>
                                    <small>{stats.rejected_dossiers || 0}</small>
                                </div>
                                <ProgressBar
                                    variant="danger"
                                    now={formatPercentage(stats.rejected_dossiers || 0, stats.total_dossiers || 1).replace('%', '')}
                                />
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Table des statistiques détaillées */}
            <Row>
                <Col>
                    <Card>
                        <Card.Header>
                            <h5>Statistiques par période</h5>
                        </Card.Header>
                        <Card.Body>
                            <Table responsive hover>
                                <thead>
                                    <tr>
                                        <th>Période</th>
                                        <th>Nouveaux utilisateurs</th>
                                        <th>Dossiers créés</th>
                                        <th>Dossiers approuvés</th>
                                        <th>Revenus</th>
                                        <th>Taux de conversion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailedStats.map((stat, index) => (
                                        <tr key={index}>
                                            <td>
                                                <Badge bg="light" text="dark">
                                                    <FaCalendarAlt className="me-1" />
                                                    {stat.period}
                                                </Badge>
                                            </td>
                                            <td>
                                                <Badge bg="primary">{stat.new_users}</Badge>
                                            </td>
                                            <td>
                                                <Badge bg="info">{stat.created_dossiers}</Badge>
                                            </td>
                                            <td>
                                                <Badge bg="success">{stat.approved_dossiers}</Badge>
                                            </td>
                                            <td>
                                                <Badge bg="success">{formatCurrency(stat.revenue)}</Badge>
                                            </td>
                                            <td>
                                                <Badge bg={stat.conversion_rate > 50 ? 'success' : 'warning'}>
                                                    {stat.conversion_rate}%
                                                </Badge>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Métriques supplémentaires */}
            <Row className="mt-4">
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h5>Métriques de performance</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col sm={6}>
                                    <div className="text-center mb-3">
                                        <FaClock className="text-info mb-2" size={24} />
                                        <h6>Temps moyen de traitement</h6>
                                        <p className="mb-0">{stats.avg_processing_time || 'N/A'} jours</p>
                                    </div>
                                </Col>
                                <Col sm={6}>
                                    <div className="text-center mb-3">
                                        <FaUsers className="text-primary mb-2" size={24} />
                                        <h6>Utilisateurs actifs</h6>
                                        <p className="mb-0">{stats.active_users || 0}</p>
                                    </div>
                                </Col>
                            </Row>
                            <hr />
                            <Row>
                                <Col sm={6}>
                                    <div className="text-center mb-3">
                                        <FaCheckCircle className="text-success mb-2" size={24} />
                                        <h6>Taux de satisfaction</h6>
                                        <p className="mb-0">{stats.satisfaction_rate || 'N/A'}%</p>
                                    </div>
                                </Col>
                                <Col sm={6}>
                                    <div className="text-center mb-3">
                                        <FaTimesCircle className="text-danger mb-2" size={24} />
                                        <h6>Taux de rejet</h6>
                                        <p className="mb-0">{formatPercentage(stats.rejected_dossiers || 0, stats.total_dossiers || 1)}</p>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={6}>
                    <Card>
                        <Card.Header>
                            <h5>Revenus par source</h5>
                        </Card.Header>
                        <Card.Body>
                            {revenueData.map((source, index) => (
                                <div key={index} className="mb-3">
                                    <div className="d-flex justify-content-between mb-1">
                                        <small>{source.source}</small>
                                        <small>{formatCurrency(source.amount)}</small>
                                    </div>
                                    <ProgressBar
                                        variant="success"
                                        now={(source.amount / (stats.total_revenue || 1)) * 100}
                                    />
                                </div>
                            ))}
                            {revenueData.length === 0 && (
                                <Alert variant="info" className="text-center">
                                    <FaEuroSign className="me-2" />
                                    Aucune donnée de revenus disponible
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default SuperAdminStatistics;
